import React, { useEffect, useRef, useState } from 'react';
import { Input, InputTypes, InputVariants, InputStates } from '@components/atoms/Input';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Typography } from '@components/atoms/Typography';
import { useTranslation } from 'react-i18next';
import { Form } from '@components/atoms/Form';
import { useData } from '@hooks/useData';
import { useOutletContext } from 'react-router-dom';
import { useRegistrationDocumentUploadStep } from '@services/useRegistrationDocumentUploadStep';
import { useNotifications } from '@hooks/useNotifications';

export type RegistrationThirdStepSuccessResponse = {
    message: string;
    documents: {
        CC: string;
        INS: string;
        DUA: string;
    };
};

export interface DocsStepContext {
    data: { [key: string]: File | null };
    onChange: (field: string, value: File | null) => void;
    onNext: () => void;
    onBack: () => void;
}

interface DocumentUploadErrors {
    CC: string[];
    INS: string[];
    DUA: string[];
}

export const DocumentUploadStep: React.FC = () => {
    const { t } = useTranslation();
    const { showDefaultNotification } = useNotifications();
    const formRef = useRef<HTMLFormElement>(null);
    const maxFileSize = 2.1 * 1024 * 1024;
    const { request: uploadDocuments, error, validationErrors, lastResponseTime } = useRegistrationDocumentUploadStep();

    const [errors, setErrors] = useState<DocumentUploadErrors>({
        CC: [],
        INS: [],
        DUA: [],
    });

    const { draft, documentStep } = useData();

    // prevents from crashing in unit tests or storybook
    const context = useOutletContext<DocsStepContext | null>();
    const onNext = context?.onNext || (() => {});
    const onBack = context?.onBack || (() => {});
    const onChange = context?.onChange || (() => {});

    const [files, setFiles] = useState<{ CC?: File; INS?: File; DUA?: File }>({
        CC: undefined,
        INS: undefined,
        DUA: undefined,
    });

    const translations = {
        prev: t('navigation.prev'),
        next: t('navigation.next'),
        title: t('document-upload-step.title'),
        CC: t('document-upload-step.card'),
        INS: t('document-upload-step.insurance'),
        DUA: t('document-upload-step.autoDoc'),
        selected: t('document-upload-step.selected'),
        fileError: t('document-upload-step.fileError'),
        fileRequired: t('document-upload-step.fileRequired'),
        fileTooLarge: t('document-upload-step.fileTooLarge'),
    };

    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];

    const validateForm = (): boolean => {
        const newErrors: DocumentUploadErrors = {
            CC: [],
            INS: [],
            DUA: [],
        };
        let isValid = true;

        (['CC', 'INS', 'DUA'] as const).forEach((key) => {
            const file = files[key];
            const existingFile = documentStep?.[key] || null;

            if (!file && !existingFile) {
                newErrors[key] = [translations.fileRequired];
                isValid = false;
            } else if (file) {
                if (!allowedTypes.includes(file.type)) {
                    newErrors[key] = [translations.fileError];
                    isValid = false;
                } else if (file.size > maxFileSize) {
                    newErrors[key] = [translations.fileTooLarge];
                    isValid = false;
                } else {
                    newErrors[key] = [];
                }
            } else {
                newErrors[key] = [];
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    const submitForm = () => {
        const isValid = validateForm();
        if (!isValid) return;

        if (!draft?.uuid) return;

        const documentsToUpload = (['CC', 'INS', 'DUA'] as const)
            .map((key) => {
                const file = files[key];
                return file ? { type: key, file } : null;
            })
            .filter(Boolean) as { type: string; file: File }[];

        uploadDocuments({
            uuid: draft.uuid,
            documents: documentsToUpload,
            language: 'pt',
        });
    };

    useEffect(() => {
        if (lastResponseTime) {
            if (validationErrors) {
                setErrors({
                    // match form request errors with frontend errors object
                    CC: validationErrors['documents.0.file'] || [],
                    INS: validationErrors['documents.1.file'] || [],
                    DUA: validationErrors['documents.2.file'] || [],
                });
            } else if (error) {
                showDefaultNotification({ type: 'error', message: error });
            } else {
                onNext();
            }
        }
    }, [lastResponseTime]);

    const handleFileChange = (key: string) => (fileList: FileList) => {
        const file = fileList?.[0] || null;

        setFiles((prev) => ({ ...prev, [key]: file || undefined }));
        onChange(key, file);

        setErrors((prev) => ({
            ...prev,
            [key]: file
                ? !allowedTypes.includes(file.type)
                    ? [translations.fileError]
                    : file.size > maxFileSize
                      ? [translations.fileTooLarge]
                      : ''
                : [translations.fileRequired],
        }));
    };

    return (
        <Form formRef={formRef} classes="d-flex flex-column gap-2" onEnterPress={submitForm} onSubmit={() => submitForm()}>
            <>
                <Typography align="center" fontSize="16" fontWeight="400" color="muted">
                    {translations.title}
                </Typography>

                {(['CC', 'INS', 'DUA'] as const).map((key) => {
                    return (
                        <div key={key}>
                            <Input
                                type={InputTypes.file}
                                variant={InputVariants.labeledInput}
                                label={`${translations[key]}*`}
                                value={files[key]?.name || ''}
                                onChange={handleFileChange(key)}
                                state={errors[key].length > 0 ? InputStates.error : undefined}
                                errorMessages={errors[key]}
                            />

                            <>
                                {documentStep?.[key] && (
                                    <Typography classes="mb-3" fontSize="14" color="success">
                                        {t('document-upload-step.uploadedFile')}
                                    </Typography>
                                )}
                            </>
                        </div>
                    );
                })}
                <Button onClick={onBack} label={translations.prev} mode={ButtonMode.primary} outline />
                <Button onClick={submitForm} label={translations.next} mode={ButtonMode.primary} />
            </>
        </Form>
    );
};
